import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  CalendarToday as CalendarMonthIcon,
} from "@mui/icons-material";
import { useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import forge from "node-forge";
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: "hidden",
};

function extraerFechaValidez(arrayBuffer, password) {
  try {
    const bytes = forge.util.createBuffer(new Uint8Array(arrayBuffer));
    const asn1 = forge.asn1.fromDer(bytes);
    const p12 = forge.pkcs12.pkcs12FromAsn1(asn1, false, password);

    const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
    const certs = certBags[forge.pki.oids.certBag];

    if (!certs || certs.length === 0) {
      return { error: "No se encontraron certificados en el archivo .p12" };
    }

    const cert = certs[0].cert;
    return {
      validoDesde: cert.validity.notBefore,
      validoHasta: cert.validity.notAfter,
      subject: cert.subject.getField("CN")?.value || "",
      issuer: cert.issuer.getField("CN")?.value || "",
    };
  } catch (e) {
    const msg = (e.message || "").toLowerCase();
    if (msg.includes("invalid password") || msg.includes("mac") || msg.includes("decrypt")) {
      return { error: "Clave del certificado incorrecta" };
    }
    return { error: "Error al leer el archivo .p12: " + (e.message || "formato inválido") };
  }
}

function formatearFecha(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("es-DO", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function certificadoVigente(validoHasta) {
  if (!validoHasta) return false;
  return new Date(validoHasta) > new Date();
}

const SubirCertificado = () => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal, activo: multiempresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const [controller] = useMaterialUIController();
  const { usuarioLog, claveLog } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || "";

  const [archivo, setArchivo] = useState(null);
  const [archivoBuffer, setArchivoBuffer] = useState(null);
  const [claveCertificado, setClaveCertificado] = useState("");
  const [mostrarClave, setMostrarClave] = useState(false);
  const [tipoEcf, setTipoEcf] = useState("certECF");
  const [procesando, setProcesando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [infoCertificado, setInfoCertificado] = useState(null);
  const [estadoServidor, setEstadoServidor] = useState(null);
  const [exito, setExito] = useState("");
  const [error, setError] = useState("");

  // Estado del certificado ya guardado en el servidor (BD central, cifrado).
  const consultarEstado = useCallback(async () => {
    if (!usuarioLog) return;
    try {
      const response = await fetch(servidor + "/api/fiscal_estadocertificado", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuario: usuarioLog || "", password: claveLog || "", empresa: empresaFiscal }),
      });
      if (response.ok) {
        setEstadoServidor(await response.json());
      }
    } catch (_e) {
      // sin conexión: la pantalla sigue sirviendo para verificar el .p12 localmente
    }
  }, [servidor, usuarioLog, claveLog]);

  useEffect(() => {
    consultarEstado();
  }, [consultarEstado]);

  // Sube el .p12 (base64) al servidor, que lo valida de nuevo y lo guarda
  // cifrado en la BD central. La verificación local con node-forge es previa
  // pero el servidor es la autoridad (revalida contraseña y vigencia).
  const handleGuardar = async () => {
    if (!archivoBuffer) {
      setError("Seleccione un archivo .p12 primero");
      return;
    }
    if (!claveCertificado) {
      setError("Ingrese la clave del certificado");
      return;
    }
    setGuardando(true);
    setError("");
    setExito("");
    try {
      const bytes = new Uint8Array(archivoBuffer);
      let binario = "";
      for (let i = 0; i < bytes.length; i += 1) binario += String.fromCharCode(bytes[i]);
      const p12Base64 = btoa(binario);

      const response = await fetch(servidor + "/api/fiscal_subircertificado", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usuario: usuarioLog || "",
          password: claveLog || "", empresa: empresaFiscal,
          p12: p12Base64,
          clavecertificado: claveCertificado,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setExito(`Certificado guardado. Vigente hasta ${data.vigencia_hasta || "—"}.`);
        consultarEstado();
      } else {
        setError(data.Error || "Error al guardar el certificado");
      }
    } catch (_e) {
      setError("Error de conexión al guardar el certificado");
    } finally {
      setGuardando(false);
    }
  };

  const handleSeleccionArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".p12") && !file.name.toLowerCase().endsWith(".pfx")) {
      setError("Solo se permiten archivos .p12 o .pfx");
      return;
    }

    setArchivo(file);
    setError("");
    setInfoCertificado(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      setArchivoBuffer(ev.target.result);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExtraerInfo = () => {
    if (!archivoBuffer) {
      setError("Seleccione un archivo .p12 primero");
      return;
    }
    if (!claveCertificado) {
      setError("Ingrese la clave del certificado");
      return;
    }

    setProcesando(true);
    setError("");
    setInfoCertificado(null);

    setTimeout(() => {
      const resultado = extraerFechaValidez(archivoBuffer, claveCertificado);
      setProcesando(false);

      if (resultado.error) {
        setError(resultado.error);
      } else {
        setInfoCertificado(resultado);
      }
    }, 300);
  };

  const handleLimpiar = () => {
    setArchivo(null);
    setArchivoBuffer(null);
    setClaveCertificado("");
    setTipoEcf("certECF");
    setInfoCertificado(null);
    setError("");
    setMostrarClave(false);
  };

  const vigente = infoCertificado ? certificadoVigente(infoCertificado.validoHasta) : null;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Subir Certificado Digital"
        subtitle={
          multiempresaFiscal
            ? `Certificado .p12 de ${emisorFiscal.nombre || `la empresa ${empresaFiscal}`}${
                emisorFiscal.rnc ? ` (RNC ${emisorFiscal.rnc})` : ""
              }: cada empresa firma con el suyo`
            : "Cargue su certificado .p12 y verifique su fecha de validez"
        }
        kicker="Fiscal"
      />

      <Grid container spacing={2} justifyContent="center" sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ ...cardShellSx, display: "flex", flexDirection: "column" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2.5, pb: 1.2, borderBottom: `1px solid ${FP.color.border}` }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.2,
                    bgcolor: FP.color.accentBlueSoft,
                    color: FP.color.accentBlue,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <VpnKeyIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
                  Certificado Digital (.p12)
                </Typography>
              </Stack>

              {/* Zona de carga del archivo */}
              <Box
                sx={{
                  border: `2px dashed ${archivo ? FP.color.accentBlue : FP.color.border}`,
                  borderRadius: 2,
                  p: 3,
                  mb: 2.5,
                  textAlign: "center",
                  bgcolor: archivo ? FP.color.accentBlueSoft : FP.color.surfaceAlt,
                  transition: "all .2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: FP.color.accentBlue,
                    bgcolor: FP.color.accentBlueSoft,
                  },
                }}
                onClick={() => document.getElementById("input-p12").click()}
              >
                <input
                  id="input-p12"
                  type="file"
                  accept=".p12,.pfx"
                  hidden
                  onChange={handleSeleccionArchivo}
                />
                {!archivo ? (
                  <>
                    <UploadFileIcon sx={{ fontSize: 40, color: FP.color.textMuted, mb: 1 }} />
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem", fontWeight: 500 }}>
                      Haga clic para seleccionar un archivo .p12 o .pfx
                    </Typography>
                    <Typography sx={{ color: FP.color.textMuted, fontSize: "0.75rem", mt: 0.5 }}>
                      Formatos aceptados: .p12, .pfx
                    </Typography>
                  </>
                ) : (
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
                    <InsertDriveFileIcon sx={{ fontSize: 28, color: FP.color.accentBlue }} />
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.88rem" }}>
                        {archivo.name}
                      </Typography>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem" }}>
                        {(archivo.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLimpiar();
                      }}
                      sx={{ color: FP.color.danger }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Box>

              {/* Campo de clave */}
              <TextField
                fullWidth
                label="Clave del Certificado"
                type={mostrarClave ? "text" : "password"}
                value={claveCertificado}
                onChange={(e) => setClaveCertificado(e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="Ingrese la contraseña del archivo .p12"
                sx={{ mb: 2.5, ...fpInputSx }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setMostrarClave(!mostrarClave)}
                        edge="end"
                      >
                        {mostrarClave ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleExtraerInfo();
                }}
              />

              <TextField
                select
                fullWidth
                label="Tipo ECF"
                value={tipoEcf}
                onChange={(e) => setTipoEcf(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2.5, ...fpInputSx }}
              >
                <MenuItem value="certECF">certECF</MenuItem>
                <MenuItem value="testEcf">testEcf</MenuItem>
              </TextField>

              {(procesando || guardando) && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

              {/* Certificado ya guardado en el servidor */}
              {estadoServidor?.existe && !exito && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: FP.color.accentBlueSoft,
                    border: `1px solid ${FP.color.border}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <VpnKeyIcon sx={{ color: FP.color.accentBlue, fontSize: 18 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.82rem", fontWeight: 500 }}>
                    Ya hay un certificado guardado (vence {formatearFecha(estadoServidor.vigencia_hasta)}
                    {typeof estadoServidor.dias_restantes === "number"
                      ? `, ${estadoServidor.dias_restantes} días restantes`
                      : ""}
                    ). Al guardar uno nuevo se reemplaza.
                  </Typography>
                </Box>
              )}

              {/* Mensaje de éxito */}
              {exito && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "#F0FDF4",
                    border: "1px solid #BBF7D0",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 20 }} />
                  <Typography sx={{ color: "#16A34A", fontSize: "0.85rem", fontWeight: 500 }}>
                    {exito}
                  </Typography>
                </Box>
              )}

              {/* Mensaje de error */}
              {error && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "#FEF2F2",
                    border: "1px solid #FECACA",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ErrorOutlineIcon sx={{ color: "#DC2626", fontSize: 20 }} />
                  <Typography sx={{ color: "#DC2626", fontSize: "0.85rem", fontWeight: 500 }}>
                    {error}
                  </Typography>
                </Box>
              )}

              {/* Info del certificado extraída */}
              {infoCertificado && (
                <Box
                  sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: vigente ? "#F0FDF4" : "#FEF2F2",
                    border: `1px solid ${vigente ? "#BBF7D0" : "#FECACA"}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    {vigente ? (
                      <CheckCircleIcon sx={{ color: "#16A34A", fontSize: 22 }} />
                    ) : (
                      <ErrorOutlineIcon sx={{ color: "#DC2626", fontSize: 22 }} />
                    )}
                    <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: vigente ? "#16A34A" : "#DC2626" }}>
                      {vigente ? "Certificado Vigente" : "Certificado Expirado"}
                    </Typography>
                    <Chip
                      label={vigente ? "ACTIVO" : "VENCIDO"}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.66rem",
                        bgcolor: vigente ? "#DCFCE7" : "#FEE2E2",
                        color: vigente ? "#16A34A" : "#DC2626",
                      }}
                    />
                  </Stack>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography sx={{ color: FP.color.textMuted, fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 600 }}>
                        Titular (CN)
                      </Typography>
                      <Typography sx={{ color: FP.color.textPrimary, fontWeight: 600, fontSize: "0.88rem" }}>
                        {infoCertificado.subject || "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography sx={{ color: FP.color.textMuted, fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 600 }}>
                        Emisor
                      </Typography>
                      <Typography sx={{ color: FP.color.textPrimary, fontWeight: 600, fontSize: "0.88rem" }}>
                        {infoCertificado.issuer || "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ color: FP.color.textMuted, fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 600 }}>
                        Válido desde
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarMonthIcon sx={{ fontSize: 15, color: FP.color.textSecondary }} />
                        <Typography sx={{ color: FP.color.textPrimary, fontWeight: 600, fontSize: "0.85rem" }}>
                          {formatearFecha(infoCertificado.validoDesde)}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ color: FP.color.textMuted, fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 600 }}>
                        Válido hasta
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <CalendarMonthIcon sx={{ fontSize: 15, color: vigente ? "#16A34A" : "#DC2626" }} />
                        <Typography sx={{ color: vigente ? "#16A34A" : "#DC2626", fontWeight: 700, fontSize: "0.85rem" }}>
                          {formatearFecha(infoCertificado.validoHasta)}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Botones */}
              <Box sx={{ display: "flex", gap: 1.4 }}>
                <Button
                  variant="outlined"
                  onClick={handleLimpiar}
                  fullWidth
                  sx={{
                    py: 1.2,
                    textTransform: "none",
                    borderRadius: 1.4,
                    fontWeight: 700,
                    borderColor: FP.color.border,
                    color: FP.color.textPrimary,
                    bgcolor: "#FFFFFF",
                    "&:hover": { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
                  }}
                >
                  Limpiar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  onClick={handleExtraerInfo}
                  fullWidth
                  disabled={!archivo || !claveCertificado || procesando}
                  sx={{
                    py: 1.2,
                    textTransform: "none",
                    borderRadius: 1.4,
                    fontWeight: 700,
                    bgcolor: FP.color.navy,
                    color: "#FFFFFF",
                    boxShadow: "none",
                    "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                    "&.Mui-disabled": { bgcolor: FP.color.border, color: FP.color.textMuted },
                  }}
                >
                  {procesando ? "Procesando..." : "Verificar Certificado"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleGuardar}
                  fullWidth
                  disabled={!archivo || !claveCertificado || guardando}
                  sx={{
                    py: 1.2,
                    textTransform: "none",
                    borderRadius: 1.4,
                    fontWeight: 700,
                    bgcolor: FP.color.navy,
                    color: "#FFFFFF",
                    boxShadow: "none",
                    "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                    "&.Mui-disabled": { bgcolor: FP.color.border, color: FP.color.textMuted },
                  }}
                >
                  {guardando ? "Guardando..." : "Guardar"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubirCertificado;
